import type { DataStreamDefinitionId } from '@squaredup/data-streams';
import type { ConfigId, Serialised } from '@squaredup/ids';
import type { TenantId } from '@squaredup/tenants';
import { hasProperty, type MatchSpec } from '@squaredup/utilities';
import { kebabCase } from 'lodash';
import { useMutation } from 'react-query';
import { Create as CreateDataStreamDefinition } from 'services/DataStreamDefinitionService';
import { CreatePlugin } from 'services/PluginService';
import type { SaveAsPluginDependencies } from './useSaveAsPluginDependencies';

/**
 * Replace the plugin Id in a matches object.
 */
const replacePluginId = <T extends Record<string, any>>(matches: T, newPluginId: string): T => {
    if (typeof matches !== 'object' || matches === null) {
        return matches;
    }

    if (Array.isArray(matches)) {
        return matches.map((item) => replacePluginId(item, newPluginId)) as unknown as T;
    }

    return Object.fromEntries(
        Object.entries(matches).map(([key, value]): [string, unknown] => {
            if (key === 'pluginId' && value.type === 'equals') {
                return [key, { ...value, value: newPluginId }];
            }
            return [key, replacePluginId(value, newPluginId)];
        })
    ) as T;
};

/**
 * Remove the configId match criteria from a matches object.
 * Without this, the created data stream will continue to try and match the configId of the base plugin
 * instance it was saved against, which will not work for the new plugin.
 */
const removeConfigIdMatchCriteria = (matches: MatchSpec, configId: ConfigId['value']): MatchSpec => {
    if (Array.isArray(matches)) {
        return matches.map((match) => removeConfigIdMatchCriteria(match, configId)) as MatchSpec;
    }

    if (typeof matches !== 'object' || matches === null) {
        return matches;
    }

    const { sourceId, ...remainingMatches } = matches;

    // Only remove the criteria if it's the one we expect.
    if (typeof sourceId === 'object' && sourceId.type === 'equals' && sourceId.value === configId) {
        return remainingMatches;
    }

    return matches;
};

/**
 * Create a new plugin based on an existing plugin configuration.
 * E.g. create a plugin containing custom data streams based on WebAPI.
 * @param basePlugin The base plugin to use as a template for the new plugin.
 * @param basePluginConfig The base plugin configuration to use as a template for the new plugin.
 * @param tenantId (optional) - If provided, the new plugin will be restricted to this tenant.
 * @returns The created plugin.
 */
export const useSaveAsPluginMutation = ({
    basePlugin,
    basePluginConfig,
    basePluginDataStreams
}: SaveAsPluginDependencies) => {
    const baseDataSourceConfig = basePluginConfig?.config;

    const {
        mutate: saveAsPlugin,
        mutateAsync: saveAsPluginAsync,
        ...rest
    } = useMutation({
        mutationKey: ['saveAsPlugin', basePlugin?.id, basePluginConfig?.id],
        mutationFn: async ({
            displayName,
            category,
            description,
            author,
            uiConfiguration,
            uiConfigMapping,
            restrictToTenant,
            dataStreamsToCopy,
            icon,
            helpLink
        }: {
            displayName: string;
            description: string;
            author: string;
            category: string;
            uiConfiguration: object;
            uiConfigMapping: Record<string, any>;
            restrictToTenant: Serialised<TenantId> | undefined;
            dataStreamsToCopy: DataStreamDefinitionId['value'][] | undefined;
            icon: string | undefined;
            helpLink: string | undefined;
        }) => {
            if (basePlugin == null) {
                throw new Error('Cannot save as plugin before base plugin is loaded');
            }

            if (basePluginConfig == null) {
                throw new Error('Cannot save as plugin before base plugin config is loaded');
            }

            if (baseDataSourceConfig == null) {
                throw new Error('Cannot save as plugin before base data source config is loaded');
            }

            if (basePluginDataStreams == null) {
                throw new Error('Cannot save as plugin before base plugin data streams are loaded');
            }

            if (basePluginDataStreams.length === 0 || (dataStreamsToCopy != null && dataStreamsToCopy.length === 0)) {
                throw new Error('Cannot save as plugin without any data streams');
            }

            const derivedPlugin = await CreatePlugin({
                author,
                category,
                dataSources: [], // Always empty, we use the data sources from the base plugin
                description,
                displayName,
                customTypes: [], // Unsupported for now
                importNotSupported:
                    !hasProperty(basePluginConfig?.config, 'importConfig') && basePlugin.importNotSupported,
                // Replaced with the base plugin's lambda name when this record is loaded,
                // but it has to be unique for the plugin to be created.
                lambdaName: `<${kebabCase(displayName)} derived from ${basePlugin.lambdaName}>`,
                onPrem: basePlugin.onPrem,
                pluginName: kebabCase(displayName),
                supportsConfigValidation: false,
                uiConfiguration,
                version: basePlugin.version,
                basePlugin: {
                    pluginId: basePlugin.id,
                    baseDataSourceConfig,
                    uiConfigMapping
                },
                restrictToTenant,
                icon,
                /**
                 * We only support global data streams for now, so the plugin can only match
                 * data source nodes that belong to the plugin instance as that's the only
                 * scope that should be used.
                 */
                matches: {
                    __configId: {
                        type: 'equals',
                        value: '{{configId}}'
                    },
                    sourceType: {
                        type: 'equals',
                        value: 'squaredup/data-source'
                    }
                },
                externalLinks:
                    helpLink == null ? undefined : [{ url: helpLink, label: helpLink, category: 'documentation' }]
            });

            // Now that we've created the derived plugin,
            // we need to add one or more data streams from the base plugin to it.

            const derivedPluginDataStreams = await Promise.all(
                basePluginDataStreams
                    .filter((baseDS) => dataStreamsToCopy == null || dataStreamsToCopy.includes(baseDS.id))
                    .map((baseDS) => {
                        return CreateDataStreamDefinition({
                            ...baseDS,
                            definition: {
                                ...baseDS.definition,
                                matches:
                                    baseDS.definition.matches == null || typeof baseDS.definition.matches !== 'object'
                                        ? undefined
                                        : removeConfigIdMatchCriteria(
                                              replacePluginId(baseDS.definition.matches, derivedPlugin.id),
                                              basePluginConfig.id
                                          )
                            },
                            pluginId: derivedPlugin.id,
                            parentPluginId: derivedPlugin.id,
                            parentPluginVersion: derivedPlugin.version
                        });
                    })
            );

            return {
                derivedPlugin,
                derivedPluginDataStreams: derivedPluginDataStreams.map(({ id }) => id)
            };
        }
    });

    return {
        saveAsPlugin,
        saveAsPluginAsync,
        ...rest
    };
};
